import React, { useState, useEffect, useCallback } from 'react';

const IntroSection = ({ isDarkMode, toggleOverlay, isMobile }) => {
    const [displayText, setDisplayText] = useState("");
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isDeleting, setIsDeleting] = useState(false);
    const [hoverPosition, setHoverPosition] = useState({ section: null, index: null });

    const greetings = [
        { text: "hey", lang: "English" },
        { text: "नमस्ते", lang: "Hindi" },
        { text: "bonjour", lang: "French" },
        { text: "hola", lang: "Spanish" },
        { text: "ನಮಸ್ಕಾರ", lang: "Kannada" },
        { text: "こんにちは", lang: "Japanese" },
    ];

    // Typing animation
    useEffect(() => {
        let timeout;
        const currentWord = greetings[currentIndex].text;

        const updateText = () => {
            if (!isDeleting) {
                if (displayText.length < currentWord.length) {
                    setDisplayText(currentWord.slice(0, displayText.length + 1));
                    timeout = setTimeout(updateText, 150);
                } else {
                    timeout = setTimeout(() => setIsDeleting(true), 1000);
                }
            } else {
                if (displayText.length > 0) {
                    setDisplayText(displayText.slice(0, -1));
                    timeout = setTimeout(updateText, 100);
                } else {
                    setIsDeleting(false);
                    setCurrentIndex((prevIndex) => (prevIndex + 1) % greetings.length);
                }
            }
        };

        timeout = setTimeout(updateText, 100);
        return () => clearTimeout(timeout);
    }, [currentIndex, displayText, isDeleting]);

    // Letter hover effect
    const handleLetterHover = useCallback((section, index) => {
        if (!isMobile) {
            setHoverPosition({ section, index });
        }
    }, [isMobile]);

    const handleMouseLeave = useCallback(() => {
        if (!isMobile) {
            setHoverPosition({ section: null, index: null });
        }
    }, [isMobile]);

    const Letter = ({ letter, index, section }) => (
        <span
            className="relative inline-block"
            onMouseEnter={() => handleLetterHover(section, index)}
        >
            {hoverPosition.section === section && hoverPosition.index === index && (
                <div className="absolute left-0 top-0 w-1 h-full bg-blue-500" />
            )}
            {letter}
        </span>
    );

    return (
        <div className="px-4 sm:px-6 md:px-8 lg:px-32">
            <div className="max-w-3xl flex flex-col pt-0 pb-2">
                {/* Greeting Text */}
                <div className="h-16 sm:h-20 lg:h-24 flex items-center mb-2 sm:mb-4">
                    <span className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-mono">
                        {displayText}
                    </span>
                </div>

                {/* Name Section */}
                <div onMouseLeave={handleMouseLeave}>
                    {/* I'm */}
                    <div className="font-mono text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl mb-2">
                        {'i\'m'.split('').map((letter, index) => (
                            <Letter
                                key={index}
                                letter={letter}
                                index={index}
                                section="im"
                            />
                        ))}
                    </div>

                    {/* Name */}
                    <div className="font-mono text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl mb-6 sm:mb-8 lg:mb-12">
                        {'pushpalatha.'.split('').map((letter, index) => (
                            <Letter
                                key={index}
                                letter={letter}
                                index={index}
                                section="name"
                            />
                        ))}
                    </div>
                </div>

                {/* Bio and Button Section */}
                <div>
                    <p className="font-mono text-sm sm:text-base text-gray-400 leading-relaxed max-w-3xl mb-8 sm:mb-12">
                        I'm a designer working on the Human Interface Team
                        at Apple.
                        My passion is to create emotional experiences at the
                        intersection of art, design, and AI.
                    </p>

                    <button
                        onClick={() => toggleOverlay(true)}
                        className="font-mono px-4 sm:px-6 lg:px-8 py-2 sm:py-3 lg:py-4
                                 border rounded-full text-sm sm:text-base
                                 relative overflow-hidden group
                                 hover:bg-gray-800 hover:text-white hover:border-gray-600
                                 transition-all duration-300 ease-in-out
                                 mb-12 sm:mb-16"
                    >
                        <span className="button-text inline-block transition-transform duration-200 ease-out">
                            → say hi
                        </span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default IntroSection;